// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — Sidebar Component
// FIX for freeze: Uses local state hooks scoped properly + React.memo optimization
// 4 Pillars (+ Financial Radar) for clients, 5 sections for admin
// ══════════════════════════════════════════════════════════════════════════════

import { memo, useState, useCallback } from 'react';
import {
  Eye, LogOut, Sun, Moon, ChevronLeft, ChevronRight,
  Globe, Menu, X, Crown, RefreshCw
} from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { useAuth } from '@contexts/AuthContext';
import { CLIENT_MODULES, ADMIN_MODULES } from '@constants/modules';

// ─── Sidebar Item (memoized for perf) ────────────────────────────────────
const SidebarItem = memo(function SidebarItem({ module, isActive, isCollapsed, label, onClick }) {
  const { theme } = useTheme();
  const Icon = module.icon;

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: isCollapsed ? '10px' : '10px 14px',
        borderRadius: 8,
        cursor: 'pointer',
        marginBottom: 2,
        background: isActive ? theme.primaryDim : 'transparent',
        color: isActive ? theme.primary : theme.textSecondary,
        fontWeight: isActive ? 600 : 500,
        fontSize: 13,
        transition: 'background 0.15s, color 0.15s',
        justifyContent: isCollapsed ? 'center' : 'flex-start'
      }}
      title={isCollapsed ? label : undefined}
      onMouseEnter={e => {
        if (!isActive) e.currentTarget.style.background = theme.bgInput;
      }}
      onMouseLeave={e => {
        if (!isActive) e.currentTarget.style.background = 'transparent';
      }}
    >
      <span style={{ flexShrink: 0, display: 'flex' }}>
        <Icon size={18} />
      </span>
      {!isCollapsed && (
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {label}
        </span>
      )}
    </div>
  );
});

// ─── Sidebar Content (reusable for desktop + mobile) ─────────────────────
const SidebarContent = memo(function SidebarContent({
  isCollapsed,
  isMobile = false,
  activeView,
  onNavigate,
  features,
  isAdminView,
  onToggleAdminView
}) {
  const { theme } = useTheme();
  const { t, lang, toggleLang } = useLang();
  const { themeName, toggleTheme } = useTheme();
  const { profile, effectiveProfile, isSuperAdmin, isImpersonating, logout } = useAuth();

  // Effective features for rendering
  const visibleModules = CLIENT_MODULES.filter(m => {
    // Super admin sees all, unless impersonating (respects tenant features)
    if (isSuperAdmin && !isImpersonating) return true;
    return features?.[m.featureKey] !== false;
  });

  const navItems = (isSuperAdmin && isAdminView && !isImpersonating) ? ADMIN_MODULES : visibleModules;
  const currentProfile = effectiveProfile || profile;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden'
    }}>
      {/* Logo */}
      <div style={{
        padding: isCollapsed && !isMobile ? '20px 12px' : '20px 20px',
        borderBottom: `1px solid ${theme.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexShrink: 0
      }}>
        <div style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: `linear-gradient(135deg, ${theme.primary}, #6C8EFF)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <Eye size={18} color="#fff" />
        </div>
        {(!isCollapsed || isMobile) && (
          <div style={{ fontSize: 16, fontWeight: 800, color: theme.text, letterSpacing: -0.3 }}>
            {t.brandName}
          </div>
        )}
      </div>

      {/* User badge */}
      <div style={{
        padding: isCollapsed && !isMobile ? '12px 8px' : '12px 20px',
        flexShrink: 0
      }}>
        {(!isCollapsed || isMobile) ? (
          <div style={{
            background: theme.primaryDim,
            borderRadius: 8,
            padding: '8px 12px',
            border: `1px solid ${theme.primaryBorder}`
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: theme.primary }}>
              {isSuperAdmin && !isImpersonating ? (isAdminView ? t.superAdmin : t.clientMode) : t.customer}
            </div>
            <div style={{
              fontSize: 12,
              color: theme.textSecondary,
              marginTop: 2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {lang === 'ar' ? currentProfile?.full_name_ar : currentProfile?.full_name_en}
            </div>
          </div>
        ) : (
          <div style={{
            background: theme.primaryDim,
            borderRadius: 8,
            padding: 6,
            display: 'flex',
            justifyContent: 'center'
          }}>
            <Crown size={14} color={theme.primary} />
          </div>
        )}
      </div>

      {/* Navigation */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: isCollapsed && !isMobile ? '4px 6px' : '4px 12px'
      }}>
        {navItems.map(module => (
          <SidebarItem
            key={module.key}
            module={module}
            isActive={activeView === module.key}
            isCollapsed={isCollapsed && !isMobile}
            label={t[module.translationKey] || module.key}
            onClick={() => onNavigate(module.key)}
          />
        ))}
      </div>

      {/* Bottom controls */}
      <div style={{
        borderTop: `1px solid ${theme.border}`,
        padding: isCollapsed && !isMobile ? '12px 6px' : '12px 14px',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 4
      }}>
        {/* Switch Mode (only super admin) */}
        {isSuperAdmin && !isImpersonating && (
          <div
            onClick={() => {
              onToggleAdminView?.();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '8px 10px',
              borderRadius: 8,
              cursor: 'pointer',
              color: theme.warning,
              fontSize: 13,
              fontWeight: 600,
              justifyContent: isCollapsed && !isMobile ? 'center' : 'flex-start',
              transition: 'background 0.15s'
            }}
            onMouseEnter={e => e.currentTarget.style.background = theme.warningDim}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <RefreshCw size={16} />
            {(!isCollapsed || isMobile) && <span>{t.switchMode}</span>}
          </div>
        )}

        {/* Language toggle */}
        <div
          onClick={toggleLang}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '8px 10px',
            borderRadius: 8,
            cursor: 'pointer',
            color: theme.textSecondary,
            fontSize: 13,
            justifyContent: isCollapsed && !isMobile ? 'center' : 'flex-start'
          }}
        >
          <Globe size={16} />
          {(!isCollapsed || isMobile) && <span>{lang === 'ar' ? t.english : t.arabic}</span>}
        </div>

        {/* Theme toggle */}
        <div
          onClick={toggleTheme}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '8px 10px',
            borderRadius: 8,
            cursor: 'pointer',
            color: theme.textSecondary,
            fontSize: 13,
            justifyContent: isCollapsed && !isMobile ? 'center' : 'flex-start'
          }}
        >
          {themeName === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          {(!isCollapsed || isMobile) && (
            <span>{themeName === 'dark' ? t.lightMode : t.darkMode}</span>
          )}
        </div>

        {/* Logout */}
        <div
          onClick={logout}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '8px 10px',
            borderRadius: 8,
            cursor: 'pointer',
            color: theme.danger,
            fontSize: 13,
            justifyContent: isCollapsed && !isMobile ? 'center' : 'flex-start'
          }}
        >
          <LogOut size={16} />
          {(!isCollapsed || isMobile) && <span>{t.signOut}</span>}
        </div>
      </div>
    </div>
  );
});

// ─── Main Sidebar (with mobile + desktop modes) ──────────────────────────
export function Sidebar({ activeView, onNavigate, features = {}, isAdminView, onToggleAdminView }) {
  const { theme } = useTheme();
  const { isRTL } = useLang();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleCollapse = useCallback(() => setIsCollapsed(p => !p), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const handleNavigate = useCallback((key) => {
    onNavigate(key);
    setMobileOpen(false);
  }, [onNavigate]);

  return (
    <>
      {/* Mobile toggle button */}
      <div
        className="sidebar-mobile-toggle"
        style={{
          display: 'none',
          position: 'fixed',
          top: 14,
          [isRTL ? 'right' : 'left']: 14,
          zIndex: 10001,
          background: theme.bgCard,
          borderRadius: 8,
          padding: 8,
          border: `1px solid ${theme.border}`,
          cursor: 'pointer'
        }}
        onClick={() => setMobileOpen(p => !p)}
      >
        {mobileOpen
          ? <X size={20} color={theme.text} />
          : <Menu size={20} color={theme.text} />
        }
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="sidebar-mobile-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 9999
          }}
          onClick={closeMobile}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className="sidebar-mobile"
        style={{
          position: 'fixed',
          top: 0,
          [isRTL ? 'right' : 'left']: 0,
          bottom: 0,
          width: 280,
          background: theme.bgSecondary,
          zIndex: 10000,
          [isRTL ? 'borderLeft' : 'borderRight']: `1px solid ${theme.border}`,
          transform: mobileOpen
            ? 'translateX(0)'
            : isRTL ? 'translateX(100%)' : 'translateX(-110%)',
          transition: 'transform 0.25s ease-out'
        }}
      >
        <SidebarContent
          isCollapsed={false}
          isMobile={true}
          activeView={activeView}
          onNavigate={handleNavigate}
          features={features}
          isAdminView={isAdminView}
          onToggleAdminView={onToggleAdminView}
        />
      </div>

      {/* Desktop sidebar */}
      <div
        className="sidebar-desktop"
        style={{
          width: isCollapsed ? 64 : 260,
          flexShrink: 0,
          background: theme.bgSecondary,
          [isRTL ? 'borderLeft' : 'borderRight']: `1px solid ${theme.border}`,
          height: '100vh',
          position: 'sticky',
          top: 0,
          transition: 'width 0.25s ease-out',
          overflow: 'hidden',
          flexDirection: 'column',
          display: 'flex'
        }}
      >
        <SidebarContent
          isCollapsed={isCollapsed}
          isMobile={false}
          activeView={activeView}
          onNavigate={handleNavigate}
          features={features}
          isAdminView={isAdminView}
          onToggleAdminView={onToggleAdminView}
        />
        {/* Collapse toggle */}
        <div
          onClick={toggleCollapse}
          style={{
            position: 'absolute',
            top: 24,
            [isRTL ? 'left' : 'right']: -12,
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: theme.bgCard,
            border: `1px solid ${theme.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 10,
            boxShadow: theme.shadow
          }}
        >
          {isRTL
            ? (isCollapsed
                ? <ChevronLeft size={12} color={theme.textTertiary} />
                : <ChevronRight size={12} color={theme.textTertiary} />)
            : (isCollapsed
                ? <ChevronRight size={12} color={theme.textTertiary} />
                : <ChevronLeft size={12} color={theme.textTertiary} />)
          }
        </div>
      </div>
    </>
  );
}
